import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Sigma, Triangle, GraduationCap, Lock, Crown } from "lucide-react";
import { SECUNDARIA_TOPICS } from "../lib/constants";
import { ProfileBar } from "../components/ProfileBar";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { useEntitlement } from "../contexts/EntitlementContext";

const ICONS = { Sigma, Triangle };

// Placeholder upcoming topics (locked)
const UPCOMING_TOPICS = [
  { id: "potencias", name: "Potencias y raíces", description: "Exponentes y √" },
  { id: "regla_de_tres", name: "Regla de tres", description: "Proporciones directa e inversa" },
];

export default function SecundariaTopics({ profile, onReset }) {
  const navigate = useNavigate();
  const { entitlement } = useEntitlement();
  const entitled = !!entitlement?.entitled;
  const trialActive = !!entitlement?.trial_active;

  return (
    <PageShell>
      <ProfileBar profile={profile} onReset={onReset} />

      <div className="mt-6">
        <ClayButton variant="ghost" onClick={() => navigate("/dashboard")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mt-2"
      >
        <div className="inline-flex items-center gap-3 rounded-full px-4 py-2 mb-4 bg-[#E9E3FF]">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#7B61FF]">
            <GraduationCap className="w-5 h-5 text-white" strokeWidth={3} />
          </span>
          <span className="font-bold text-base text-[#3D2EA0]">Secundaria</span>
        </div>

        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1C1C1E] mb-2">
          Elige un tema
        </h2>
        <p className="text-base sm:text-lg text-[#5C5C60] mb-4 sm:mb-6 max-w-xl">
          Practica los temas más importantes de secundaria.
        </p>

        {!entitled && (
          <div
            className="bg-gradient-to-br from-[#FFF6CC] to-white border-2 border-[#FFD700] rounded-2xl p-4 sm:p-5 mb-6 flex items-start gap-3 sm:gap-4"
            data-testid="secundaria-premium-banner"
          >
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-[#FFD700] to-[#FF9500] flex items-center justify-center shrink-0">
              <Crown className="w-7 h-7 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-heading text-lg sm:text-xl font-bold text-[#7A5C00]">
                Secundaria es Premium
              </p>
              <p className="text-sm text-[#5C5C60] mb-3">
                Suscríbete para desbloquear Álgebra y todos los próximos temas de secundaria.
              </p>
              <ClayButton
                onClick={() => navigate("/upgrade", { state: { reason: "secundaria_locked" } })}
                size="sm"
                data-testid="secundaria-upgrade-btn"
              >
                <Crown className="w-4 h-4" strokeWidth={3} />
                Ver planes
              </ClayButton>
            </div>
          </div>
        )}

        {entitled && trialActive && (
          <div
            className="bg-[#E8F9EE] border-2 border-[#34C759] rounded-2xl p-3 mb-6 text-center"
            data-testid="secundaria-trial-banner"
          >
            <p className="text-sm font-bold text-[#1A6B2F]">
              🎁 Estás en prueba gratis · {entitlement.trial_days_left} día{entitlement.trial_days_left === 1 ? "" : "s"} restantes
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6" data-testid="secundaria-topics-grid">
          {SECUNDARIA_TOPICS.map((topic, i) => {
            const Icon = ICONS[topic.icon] || Sigma;
            const locked = !entitled;
            return (
              <motion.button
                key={topic.id}
                onClick={() =>
                  locked
                    ? navigate("/upgrade", { state: { reason: "secundaria_locked" } })
                    : navigate(topic.target)
                }
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, type: "spring", bounce: 0.4 }}
                whileHover={{ y: -4 }}
                whileTap={{ scale: 0.97 }}
                className="clay-btn relative rounded-[2rem] p-5 sm:p-6 text-left text-white border-b-[8px] active:border-b-0 overflow-hidden"
                style={{ backgroundColor: topic.color, borderBottomColor: topic.borderDark }}
                data-testid={`secundaria-topic-${topic.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-white/25 backdrop-blur flex items-center justify-center">
                    <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={2.5} />
                  </div>
                  {locked && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-white/25 px-2 py-1 rounded-full">
                      <Lock className="w-3 h-3" strokeWidth={3} />
                      Premium
                    </span>
                  )}
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-1">{topic.name}</h3>
                <p className="text-sm sm:text-base text-white/85">{topic.description}</p>
              </motion.button>
            );
          })}

          {UPCOMING_TOPICS.map((topic, i) => (
            <motion.div
              key={topic.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (SECUNDARIA_TOPICS.length + i) * 0.08, type: "spring", bounce: 0.4 }}
              className="rounded-[2rem] p-5 sm:p-6 text-left bg-gray-50 border-4 border-gray-100 opacity-70"
              data-testid={`secundaria-upcoming-${topic.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gray-200 flex items-center justify-center">
                  <Sigma className="w-7 h-7 text-gray-400" strokeWidth={2.5} />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider bg-white text-gray-500 px-2 py-1 rounded-full">
                  Próximamente
                </span>
              </div>
              <h3 className="font-heading text-2xl font-bold text-gray-500 mb-1">{topic.name}</h3>
              <p className="text-sm text-gray-400">{topic.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </PageShell>
  );
}
